import { create } from "zustand";

import { t } from "../contract/strings";

// The default theme is first in the type but second in the list: on the dial the chosen
// card is centred and its neighbours peek above and below, so "Original teal" opening in
// the middle slot shows a card on either side of it instead of an empty one above.
export const themes = [
  { id: "espresso", name: t("theme.espresso") },
  { id: "original", name: t("theme.original") },
  { id: "honey", name: t("theme.honey") },
  { id: "sage", name: t("theme.sage") },
  { id: "navy", name: t("theme.navy") },
  { id: "terracotta", name: t("theme.terracotta") },
  { id: "aubergine", name: t("theme.aubergine") },
  { id: "rose", name: t("theme.rose") },
] as const;

export type ThemeId = (typeof themes)[number]["id"];
const storageKey = "meetfolks-theme";

function isTheme(value: unknown): value is ThemeId {
  return themes.some((theme) => theme.id === value);
}

// Launcher and browser icons keep the black default (#904). In-app brand marks use the
// current accent and accent-ink to shade their three folded planes independently.
function applyTheme(theme: ThemeId) {
  document.documentElement.dataset.theme = theme;
  const accent = getComputedStyle(document.documentElement).getPropertyValue("--accent").trim();
  if (!accent) return;
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", accent);
}

export const useTheme = create<{ theme: ThemeId; selectTheme: (theme: ThemeId) => void }>((set) => ({
  theme: "original",
  selectTheme: (theme) => {
    applyTheme(theme);
    set({ theme });
    try {
      localStorage.setItem(storageKey, theme);
    } catch {
      // The live selection still works when browser storage is unavailable.
    }
  },
}));

/** Restore before React mounts, including when opening a route directly. */
export function initializeTheme() {
  let theme: ThemeId = "original";
  try {
    const saved = localStorage.getItem(storageKey);
    if (isTheme(saved)) theme = saved;
  } catch {
    // Use the original palette if storage is unavailable.
  }
  applyTheme(theme);
  useTheme.setState({ theme });
}
